import { BaseComponent } from '../core/BaseComponent.js';
import { apiClient } from '../utils/ApiClient.js';

/**
 * LoginForm - Handles custom login with OTP functionality
 */
export class LoginForm extends BaseComponent {
    constructor(selector, options = {}) {
        super(selector, options);
        
        this.isOtpStep = false;
        this.userId = null;
        this.apiAction = options.apiAction || 'custom_ajax_login';
    }

    init() {
        if (!super.init()) return this;
        
        this.cacheElements();
        this.setupEventListeners();
        
        return this;
    }

    cacheElements() {
        this.messageDiv = this.$('#login-message');
        this.usernameInput = this.$('#user_login');
        this.passwordInput = this.$('#user_pass');
        this.otpInput = this.$('#otp');
        this.rememberCheckbox = this.$('#rememberme');
        this.redirectInput = this.$('#redirect_url');
        this.nonceInput = this.$('input[name="custom_login_nonce"]');
        this.formGroups = this.$$('.form-group');
        this.formOptions = this.$('.form-options');
        this.otpForm = this.$('#otp-form');
        this.submitBtn = this.$('.sign-in-btn');
        this.resendBtn = this.$('#otp-resend-btn');
        this.resendCountdown = this.$('#otp-resend-countdown');
        this._countdownTimer = null;
    }

    setupEventListeners() {
        this.element.addEventListener('submit', (e) => this.handleSubmit(e));
        if (this.resendBtn) {
            this.resendBtn.addEventListener('click', () => this.handleResend());
        }
    }

    async handleSubmit(e) {
        e.preventDefault();

        if (this.isOtpStep) {
            await this.handleOtpVerification();
        } else {
            await this.handleInitialLogin();
        }
    }

    /**
     * Handle initial login submission
     */
    async handleInitialLogin() {
        const username = this.usernameInput.value;
        const password = this.passwordInput.value;
        const remember = this.rememberCheckbox.checked ? 1 : 0;
        const nonce = this.nonceInput.value;
        const redirectUrl = this.redirectInput.value;

        this.setLoading(true);

        try {
            const response = await apiClient.post(this.apiAction, {
                username,
                password,
                remember,
                nonce,
                redirect_url: redirectUrl
            });

            if (response.success && response.data.show_otp) {
                this.showOtpForm(response.data.user_id);
            } else if (response.success) {
                window.location.href = response.data.redirect;
            } else {
                this.showError(response.data?.message || 'Login failed.');
            }
        } catch (error) {
            console.error('Login error:', error);
            this.showError('Something went wrong.');
        } finally {
            this.setLoading(false);
        }
    }

    /**
     * Handle OTP verification
     */
    async handleOtpVerification() {
        const otp = this.otpInput.value;
        const remember = this.rememberCheckbox.checked ? 1 : 0;
        const redirectUrl = this.redirectInput.value;
        const nonce = this.nonceInput.value;

        this.setLoading(true);

        try {
            const response = await apiClient.post(this.apiAction, {
                otp,
                remember,
                nonce,
                user_id: this.userId,
                redirect_url: redirectUrl
            });

            if (response.success) {
                window.location.href = response.data.redirect;
            } else {
                this.showError(response.data?.message || 'OTP verification failed.');
            }
        } catch (error) {
            console.error('OTP verification error:', error);
            this.showError('OTP verification failed.');
        } finally {
            this.setLoading(false);
        }
    }

    /**
     * Show OTP input form
     */
    showOtpForm(userId) {
        this.userId = userId;
        this.isOtpStep = true;

        // Hide login form fields
        if (this.messageDiv) {
            this.messageDiv.style.display = 'none';
        }

        this.formGroups.forEach(group => {
            group.style.display = 'none';
        });

        if (this.formOptions) {
            this.formOptions.style.display = 'none';
        }

        // Show OTP form
        if (this.otpForm) {
            this.otpForm.style.display = 'block';
            const otpFormGroup = this.otpForm.querySelector('.form-group');
            if (otpFormGroup) {
                otpFormGroup.style.display = 'block';
            }
        }

        // Update submit button text
        if (this.submitBtn) {
            this.submitBtn.innerHTML = 'Submit';
        }

        // Start 60s countdown before enabling resend
        this.startResendCountdown(60);
    }

    /**
     * Start countdown timer for resend button
     */
    startResendCountdown(seconds) {
        if (!this.resendBtn) return;

        this.resendBtn.disabled = true;
        let remaining = seconds;

        const tick = () => {
            if (this.resendCountdown) {
                this.resendCountdown.textContent = `(${remaining}s)`;
            }
            if (remaining <= 0) {
                this.resendBtn.disabled = false;
                if (this.resendCountdown) this.resendCountdown.textContent = '';
                return;
            }
            remaining--;
            this._countdownTimer = setTimeout(tick, 1000);
        };

        clearTimeout(this._countdownTimer);
        tick();
    }

    /**
     * Handle resend OTP click
     */
    async handleResend() {
        if (!this.userId || !this.resendBtn) return;

        this.resendBtn.disabled = true;
        if (this.resendCountdown) this.resendCountdown.textContent = '';

        try {
            const response = await apiClient.post('docuswift_resend_otp', {
                user_id: this.userId,
                nonce: this.nonceInput?.value || '',
            });

            if (response.success) {
                if (this.messageDiv) {
                    this.messageDiv.style.display = 'block';
                    this.messageDiv.classList.remove('error');
                    this.messageDiv.style.color = '#3d8c57';
                    this.messageDiv.textContent = response.data?.message || 'Code resent.';
                }
                this.startResendCountdown(60);
            } else {
                this.showError(response.data?.message || 'Failed to resend code.');
                this.startResendCountdown(15);
            }
        } catch (error) {
            console.error('Resend OTP error:', error);
            this.showError('Something went wrong. Please try again.');
            this.resendBtn.disabled = false;
        }
    }

    /**
     * Toggle the loading state on the submit button.
     * Adds the shared `button.loading` class which hides the text via
     * `color: transparent` and shows a CSS spinner via `::after`, keeping the
     * button dimensions stable so the layout doesn't shift.
     *
     * @param {boolean} loading
     */
    setLoading(loading) {
        if (!this.submitBtn) return;

        if (loading) {
            this.submitBtn.classList.add('loading');
            this.submitBtn.disabled = true;
        } else {
            this.submitBtn.classList.remove('loading');
            this.submitBtn.disabled = false;
        }
    }

    /**
     * Show error message
     */
    showError(message) {
        if (this.messageDiv) {
            this.messageDiv.style.display = 'block';
            this.messageDiv.style.color = '';
            this.messageDiv.classList.add('error');
            this.messageDiv.textContent = message;
        }
    }

    /**
     * Clear error message
     */
    clearError() {
        if (this.messageDiv) {
            this.messageDiv.style.display = 'none';
            this.messageDiv.classList.remove('error');
            this.messageDiv.textContent = '';
        }
    }
}

